import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { logWarn } from "@/lib/logger";

/** Handles WS connection lifecycle for the intermediation chat.
 * Returns { status, onMessage } — `onMessage` fires when a WS-pushed message arrives. */
export default function useChatWebSocket(contractId, { enabled, onMessage }) {
  const [status, setStatus] = useState("disconnected"); // connecting | live | disconnected
  const wsRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(async () => {
    if (!contractId) return;
    try {
      setStatus("connecting");
      const { data } = await api.post("/auth/ws-token");
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = backend.replace(/^http/, "ws");
      const url = `${wsBase}/api/ws/chat/${contractId}?token=${encodeURIComponent(data.ws_token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => setStatus("live");
      ws.onclose = () => setStatus("disconnected");
      ws.onerror = () => setStatus("disconnected");
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "system") return;
          onMessageRef.current?.(msg);
        } catch (e) {
          logWarn("chat WS: malformed frame", e);
        }
      };
    } catch (e) {
      logWarn("chat WS: failed to obtain token or connect", e);
      setStatus("disconnected");
    }
  }, [contractId]);

  useEffect(() => {
    if (enabled && contractId) connect();
    return () => {
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) { logWarn("chat WS: close error", e); }
        wsRef.current = null;
      }
      setStatus("disconnected");
    };
  }, [enabled, contractId, connect]);

  return { status };
}
